<template>
  <div
    ref="scrollElement"
    :class="[
      'scroll-fade-in',
      `fade-${direction}`,
      { 'is-visible': isVisible }
    ]"
    :style="customStyles"
  >
    <slot></slot>
  </div>
</template>

<script>
export default {
  name: 'ScrollFadeIn',
  props: {
    // 淡入方向：up(从下往上), down(从上往下), left(从右往左), right(从左往右)
    direction: {
      type: String,
      default: 'up',
      validator: value => ['up', 'down', 'left', 'right'].includes(value)
    },
    // 移动距离(px)
    distance: {
      type: Number,
      default: 30
    },
    // 动画持续时间(ms)
    duration: {
      type: Number,
      default: 600
    },
    // 动画延迟(ms)
    delay: {
      type: Number,
      default: 0
    },
    // 触发阈值(0-1)
    threshold: {
      type: Number,
      default: 0.1
    },
    // 根边距偏移
    rootMargin: {
      type: String,
      default: '0px 0px -50px 0px'
    },
    // 缓动函数
    easing: {
      type: String,
      default: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)'
    },
    // 是否只触发一次
    once: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      isVisible: false,
      observer: null
    }
  },
  computed: {
    customStyles() {
      return {
        '--fade-distance': `${this.distance}px`,
        '--fade-duration': `${this.duration}ms`,
        '--fade-delay': `${this.delay}ms`,
        '--fade-easing': this.easing
      }
    }
  },
  mounted() {
    this.initObserver()
  },
  beforeDestroy() {
    this.cleanupObserver()
  },
  methods: {
    initObserver() {
      // 检查浏览器是否支持Intersection Observer
      if (!window.IntersectionObserver) {
        // 降级处理：直接显示元素
        this.isVisible = true
        return
      }

      const options = {
        threshold: this.threshold,
        rootMargin: this.rootMargin
      }

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.isVisible = true
            this.$emit('visible', entry)
            
            // 如果设置为只触发一次，则停止观察
            if (this.once) {
              this.observer.unobserve(entry.target)
            }
          } else if (!this.once) {
            // 如果不是只触发一次，则支持重复显示/隐藏
            this.isVisible = false
            this.$emit('hidden', entry)
          }
        })
      }, options)

      this.observer.observe(this.$refs.scrollElement)
    },
    
    cleanupObserver() {
      if (this.observer) {
        this.observer.disconnect()
        this.observer = null
      }
    },

    // 手动触发显示
    show() {
      this.isVisible = true
      this.$emit('show')
    },

    // 手动触发隐藏
    hide() {
      this.isVisible = false
      this.$emit('hide')
    },

    // 重置状态
    reset() {
      this.isVisible = false
      if (this.observer && this.$refs.scrollElement) {
        this.observer.unobserve(this.$refs.scrollElement)
        this.observer.observe(this.$refs.scrollElement)
      }
      this.$emit('reset')
    }
  }
}
</script>

<style scoped>
.scroll-fade-in {
  transition: all var(--fade-duration) var(--fade-easing) var(--fade-delay);
  opacity: 0;
}

/* 从下往上淡入 */
.fade-up {
  transform: translateY(var(--fade-distance));
}

.fade-up.is-visible {
  opacity: 1;
  transform: translateY(0);
}

/* 从上往下淡入 */
.fade-down {
  transform: translateY(calc(-1 * var(--fade-distance)));
}

.fade-down.is-visible {
  opacity: 1;
  transform: translateY(0);
}

/* 从右往左淡入 */
.fade-left {
  transform: translateX(var(--fade-distance));
}

.fade-left.is-visible {
  opacity: 1;
  transform: translateX(0);
}

/* 从左往右淡入 */
.fade-right {
  transform: translateX(calc(-1 * var(--fade-distance)));
}

.fade-right.is-visible {
  opacity: 1;
  transform: translateX(0);
}

/* 响应式适配 */
@media (max-width: 768px) {
  .scroll-fade-in {
    --fade-distance: 20px;
  }
}

/* 减弱的动画效果（用户偏好） */
@media (prefers-reduced-motion: reduce) {
  .scroll-fade-in {
    transition: opacity 0.3s ease;
    transform: none !important;
  }
}
</style>